import React from 'react';
import classNames from 'classnames';
import * as FormProperties from './properties-config';
import { FormFieldBase, getFieldProperties, getFieldPropertyValues } from './';
import { FormFieldInputRadio } from './form-field-inputs';
import ValidationMessage from './validation-message';
import { getOptionsHashMap, getOptionValue } from './helpers';

export const FormFieldSingle = ({
  field,
  className,
  commonProps,
  isRenderedField,
  onChange,
  value,
  onBlur,
  validationMessage,
  readOnly,
}) => {
  // import of 'utils' breaks unit tests so for now, just including
  // local version of check-for-empty logic
  const isEmpty = val => {
    const isObject = typeof val === 'object';
    const isArray = isObject && Array.isArray(val);
    let result = false;

    result =
      val === undefined ||
      val === null ||
      val === '' ||
      (isObject && Object.keys(val).length === 0) ||
      (isArray && val.length === 0);

    return result;
  };

  const optionMatchesValue = (options, option, value, index) => {
    const optionLabel = getOptionValue(option, 'alt');
    const optionsHashMap = getOptionsHashMap(options);
    const hashMapItem = optionsHashMap[optionLabel];
    const isDuplicated = hashMapItem && Object.keys(hashMapItem).length > 1;
    const includeIndexInValue = isDuplicated || !optionLabel;

    return includeIndexInValue
      ? value === `${optionLabel}-${index}`
      : value?.trim() === optionLabel?.trim();
  }

  // if value isn't found in the list of options, then this is an "other" value
  const valueIsOther = (options, value) => {
    const foundOption = options.find((option, index) => {
      return optionMatchesValue(options, option, value, index);
    });
    const valueNotAnOption = foundOption === undefined;
    return valueNotAnOption && !isEmpty(value);
  };

  const handleChange = (e, group, label, options, index) => {
    const isOther = index === undefined;
    const optionsHashMap = getOptionsHashMap(options);
    const hashMapItem = optionsHashMap[label];
    const isDuplicated = hashMapItem && Object.keys(hashMapItem).length > 1;
    const optionValue = hashMapItem && getOptionValue(hashMapItem[index], 'alt');
    const includeIndexInValue = isDuplicated || !optionValue;

    let value = '';
    if (e.target.type !== 'radio') {
      value = e.target.value;
    } else if (includeIndexInValue && !isOther) {
      value = `${optionValue}-${index}`;
    } else {
      value = optionValue;
    }

    // define custom event object
    const event = {
      target: {
        id: group,
        value,
        el: e.target,
      },
    };
    onBlur({
      target: {
        id: group,
        value,
      },
    });

    onChange(event);
  };

  const localProps = new Map(FormProperties.Single);
  const propertyNames = ['options', 'displayOther', 'other', 'displayAsColumns', 'numColumns'];
  const defaultValues = propertyNames.map(propName => ({
    [`${propName}`]: localProps.get(propName).defaultValue,
  }));
  const properties = getFieldProperties(field, 'specific');
  const { options, displayOther, other, displayAsColumns, numColumns } = getFieldPropertyValues(
    properties.specific,
    propertyNames,
    defaultValues,
  );
  const classes = classNames({
    [className]: true,
    single: true,
    'control-group error': validationMessage,
  });

  // do not show the border red for 'Other' option if field has an error
  const otherCheckboxClasses = classNames({
    other: true,
    'control-group form-field-single overwrite-error': true,
  });

  // if this field is being rendered to a published form,
  // we need to add a group prop to the checkboxes to tie
  // back to the parent field
  const group = isRenderedField ? field.uuid : undefined;
  const isOther = valueIsOther(options, value);
  const otherValue = {};
  if (isOther) otherValue.value = value;

  return (
    <div className={classes}>
      <div
        className="form-field-label primary"
      >
        {`${commonProps.label} ${commonProps.makeRequired ? '*' : ''}`}
      </div>

      <div
        className="form-field-input radio"
        style={{
          gap: '1rem',
          display: displayAsColumns ? 'block' : 'flex',
          flexDirection: displayAsColumns ? 'column' : 'row',
          flexWrap: displayAsColumns ? 'nowrap' : 'wrap',
        }}
      >
        {options.map((option, index) => {
          const label = option.hasOwnProperty('src') ? (
            <img src={option.src} height={option.height} width={option.width} alt={option.alt} />
          ) : (
            option
          );

          // checked is true if the current option we're iterating on matches
          // the value stored for this option group
          // and only if we're NOT in the BUILD view
          const checked = isRenderedField && optionMatchesValue(options, option, value, index);

          // render radio input for each option
          return (
            <FormFieldInputRadio
              key={index}
              label={label}
              displayAsColumns={displayAsColumns}
              numColumns={numColumns}
              group={group}
              isRenderedField={isRenderedField}
              onChange={(e, group, label) => {
                handleChange(e, group, label, options, index);
              }}
              checked={checked}
              disabled={readOnly}
            />
          );
        })}
        {displayOther && (
          // render an 'Other' option if selected
          <FormFieldInputRadio
            className={otherCheckboxClasses}
            isOther
            label={other}
            displayAsColumns={displayAsColumns}
            numColumns={numColumns}
            group={group}
            isRenderedField={isRenderedField}
            onChange={(e, group, label) => {
              handleChange(e, group, label, options);
            }}
            disabled={readOnly}
            {...otherValue}
          />
        )}
        {commonProps.sublabel && (
          <div className='form-field-label secondary'>{commonProps.sublabel}</div>
        )}
        <ValidationMessage message={validationMessage} />
      </div>
    </div>
  );
};

FormFieldSingle.defaultProps = {
  className: '',
  commonProps: {},
  isRenderedField: false,
  onChange: () => { },
  value: '',
  onBlur: () => { },
  readOnly: false,
};

export default FormFieldBase(FormFieldSingle);

